import { useState, FormEvent } from "react";
import { Link } from "wouter";
import { Mail, Instagram, Facebook, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GratimyLogo } from "@/components/ui/gratimy-logo";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import WriteReviewModal from "@/components/write-review-modal";

export default function SiteFooter() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const onSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await apiRequest("POST", "/api/newsletter/signup", {
        email,
        gdprConsent: true,
        source: "site_footer",
      });
      toast({ title: t("footer.subscribeSuccess") });
      setEmail("");
    } catch {
      toast({ title: t("footer.subscribeError"), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto px-4 md:px-12 pt-10 pb-6 text-white" role="contentinfo">
      <div className="max-w-[1328px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand + tagline + socials */}
          <div className="lg:col-span-1">
            <GratimyLogo variant="light" size={32} />
            <p className="mt-4 text-sm text-white/80 font-body leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a href="https://www.instagram.com/gratimy?igsh=MTVkMXJpN2diYXFzdw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 inline-flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/share/1Cy1RiZ9y3/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 inline-flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product column */}
          <div>
            <h4 className="font-heading text-base mb-3 text-white">{t("footer.product")}</h4>
            <ul className="space-y-2 text-sm font-body text-white/80">
              <li><Link href="/dashboard" className="hover:text-white transition-colors">{t("footer.dashboard")}</Link></li>
              <li><Link href="/pricing" className="hover:text-white transition-colors">{t("footer.pricing")}</Link></li>
              <li><Link href="/pricing#gift" className="hover:text-white transition-colors">{t("footer.giftGratimy")}</Link></li>
              <li><Link href="/app-info" className="hover:text-white transition-colors">{t("footer.howItWorks")}</Link></li>
            </ul>
          </div>

          {/* Company column */}
          <div>
            <h4 className="font-heading text-base mb-3 text-white">{t("footer.company")}</h4>
            <ul className="space-y-2 text-sm font-body text-white/80">
              <li><Link href="/contact" className="hover:text-white transition-colors">{t("footer.contact")}</Link></li>
              <li><Link href="/privacy-policy" className="hover:text-white transition-colors">{t("footer.privacyPolicy")}</Link></li>
              <li><Link href="/terms" className="hover:text-white transition-colors">{t("footer.terms")}</Link></li>
              <li><Link href="/cookie-policy" className="hover:text-white transition-colors">{t("footer.cookies")}</Link></li>
              {user && (
                <li>
                  <button
                    type="button"
                    onClick={() => setReviewOpen(true)}
                    className="hover:text-white transition-colors text-left"
                    data-testid="button-open-write-review"
                  >
                    {t("footer.writeReview")}
                  </button>
                </li>
              )}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-base mb-3 text-white">{t("footer.newsletter")}</h4>
            <p className="text-sm text-white/80 font-body mb-3">{t("footer.newsletterDesc")}</p>
            <form onSubmit={onSubscribe} className="flex gap-2">
              <Input
                type="email"
                required
                placeholder={t("footer.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                className="bg-white/90 text-gray-900 placeholder:text-gray-500 border-0 rounded-full"
              />
              <Button
                type="submit"
                disabled={busy}
                size="icon"
                className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white shrink-0"
                aria-label={t("footer.subscribe")}
              >
                {busy ? <Mail className="w-4 h-4 animate-pulse" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/20 flex flex-col md:flex-row items-center justify-between gap-3 text-xs font-body text-white/70">
          <p>© {year} Gratimy. {t("footer.rights")}</p>
          <nav className="flex flex-wrap gap-4" aria-label="Legal">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">{t("footer.privacyPolicy")}</Link>
            <Link href="/terms" className="hover:text-white transition-colors">{t("footer.terms")}</Link>
            <Link href="/cookie-policy" className="hover:text-white transition-colors">{t("footer.cookies")}</Link>
          </nav>
        </div>
      </div>
      {user && (
        <WriteReviewModal open={reviewOpen} onOpenChange={setReviewOpen} />
      )}
    </footer>
  );
}
